import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { Link as ChakraLink, HStack, Spinner } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import type { LinkProps as ChakraLinkProps } from '@chakra-ui/react';
import type { LinkProps as RouterLinkProps } from 'react-router-dom';

export type HardLinkProps = {
    type: 'hard';
} & ChakraLinkProps;

export type SoftLinkProps = {
    type: 'soft';
} & ChakraLinkProps &
    RouterLinkProps;

export type HyperlinkProps = (HardLinkProps | SoftLinkProps) & {
    showLoader?: boolean;
};

/**
 * A link component that can be used to navigate to a new page.
 * Implements design system hyperlink style from https://www.figma.com/design/arIOXQ7jMdpTLqR6VYEsCo/sltDesignSystem?node-id=9303-42485&m=dev
 */
export const Hyperlink: FC<HyperlinkProps> = ({
    type = 'soft',
    showLoader = false,
    onClick,
    ...props
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLAnchorElement>) => {
            setIsLoading(true);
            onClick?.(e);
        },
        [onClick],
    );

    return (
        <HStack spacing={1}>
            <ChakraLink
                {...props}
                onClick={handleClick}
                as={type === 'soft' ? RouterLink : undefined}
                variant={props.variant || 'base'}
                textStyle={props.textStyle || 'link-base'}
            />
            {showLoader && isLoading ? <Spinner size="sm" /> : null}
        </HStack>
    );
};

Hyperlink.defaultProps = {
    showLoader: false,
};
